import * as React from 'react';
import { useMemo } from 'react';
import BaggageCount from '../BaggageCount/BaggageCount';
import BaggageDirection from '../BaggageDirection/BaggageDirection';
import { ThemeProvider, useTheme } from '../../../../theme';
import Baggage from '../../../Baggage';
import { BaggageItem as BaggageItemType } from '../../types';
import Money from '../../../../Money';
import { useTranslation } from 'react-i18next';
import { BaggageContext } from '../../../context';
import cn from 'classnames';
import { default as BaseButton } from '../../../../BaseComponents/Button';
import BaggageDeleteButton, { hasDeletableServices } from '../../../BaggageDeleteButton';
import { SelectedBaggageService } from '../../../types';

const PlusIcon = (
	<svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M14.5834 9.75H10.0834V14.25H8.58337V9.75H4.08337V8.25H8.58337V3.75H10.0834V8.25H14.5834V9.75Z"
			fill="currentColor"
		/>
	</svg>
);

interface Props extends BaggageItemType {
	realIconSize?: boolean;
}

const BaggageItem: React.FC<Props> = ({ baggage, servicesBySegments, realIconSize, priceFrom, selected }) => {
	const { BaggageList: css } = useTheme('BaggageList');
	const { t } = useTranslation('AdditionalBaggage');

	const baggageIconTheme = useMemo(() => ({ Baggage: { BaggageIcon: { root: css.icon } } }), [css]);
	const baggageContext = React.useContext(BaggageContext);
	const baggageIconClickHandler = baggageContext?.onBaggageIconClick;

	const canDelete = servicesBySegments.some(service => hasDeletableServices(service.selectedServices));
	const isShowDeleteButton = canDelete && !!baggageContext?.onBaggageDelete;
	const onDelete = () => {
		const servicesToDelete: SelectedBaggageService[] = [];

		servicesBySegments.forEach(service => {
			servicesToDelete.push(...(service.selectedServices ?? []));
		});

		baggageContext.onBaggageDelete(servicesToDelete);
	};

	return (
		<div className={css.item} data-test-id="baggage-item" data-rfisc={baggage[0].rfics}>
			{isShowDeleteButton && <BaggageDeleteButton className={css.deleteButton} onClick={onDelete} />}

			<ThemeProvider value={baggageIconTheme}>
				<Baggage baggage={baggage[0]} realIconSize={realIconSize} selected={selected} />
			</ThemeProvider>
			{servicesBySegments.length > 0 && (
				<div className={css.counts}>
					{servicesBySegments.map((service, index) => (
						<div className={css.count} key={index}>
							<BaggageCount className={css.baggage_count} count={service.count} />
							<BaggageDirection
								className={css.baggage_direction}
								allSegments={service.allSegments}
								directions={service.directions}
							/>
						</div>
					))}
				</div>
			)}

			{servicesBySegments.length === 0 && (
				<div className={cn(css.footer, { [css.footer_empty]: !baggage[0].canBeAdded })}>
					<BaseButton variant="optionAction" className={css.add} onClick={baggageIconClickHandler}>
						{PlusIcon}
					</BaseButton>
					<div className={css.price}>
						{t('from')}
						<Money money={priceFrom} />
					</div>
				</div>
			)}
		</div>
	);
};

export default BaggageItem;
